import { ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, signal } from '@angular/core';
import { ModalComponent } from '../modal/modal.component';
import { CookieStore } from './services/cookie-banner.store';
import { CookieFormArray, IPopupModelV2, ModalChild } from '@mwe/models';
import { FormArray, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ButtonLinkComponent } from '../buttons/button-link/button-link.component';
import { NgbActiveModal, NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';
import { SafeHtmlPipe } from '@mwe/ui/pipes';
import { Subscription, tap } from 'rxjs';
import { Collapse } from './services/mwe-collapse';
import { ButtonComponent } from '../buttons/button/button.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'mwe-cookie-consent',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './cookie.component.html',
  imports: [
    ModalComponent,
    NgbCollapseModule,
    ButtonLinkComponent,
    ReactiveFormsModule,
    SafeHtmlPipe,
    SafeHtmlPipe,
    Collapse,
    ButtonComponent,
    TranslatePipe,
  ],
})
export class CookieComponent implements ModalChild, OnDestroy {
  popupModel = signal<IPopupModelV2>(null);

  private formBuilder = inject(FormBuilder);
  formGroup = this.formBuilder.group({
    cookieGroups: this.formBuilder.array<CookieFormArray>([]),
  });

  private subscription = new Subscription();

  private readonly cookieStore = inject(CookieStore);
  groups = computed(() => this.cookieStore.groups());

  cookieGroups = signal<FormArray<CookieFormArray>>(null);
  private activeModal = inject(NgbActiveModal);

  constructor() {
    effect(() => {
      this.createCookieGroups();
      this.handleFormUpdates();
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  get cookieFormContents() {
    return this.formGroup.value?.cookieGroups?.map(group => {
      const acceptedServices = group?.services?.filter(service => service.accepted) || [];
      return {
        id: group?.id || '',
        accepted: group?.accepted || false,
        services: acceptedServices,
      };
    });
  }

  getGroupData(index: number) {
    const groups = this.groups();
    return groups?.[index] || null;
  }

  getServiceData(groupIndex: number, serviceIndex: number) {
    const group = this.getGroupData(groupIndex);
    return group?.services?.[serviceIndex] || null;
  }

  getServicesArray(groupIndex: number) {
    return this.cookieGroups()?.at(groupIndex)?.get('services') as unknown as FormArray<CookieFormArray>;
  }

  acceptSelection(acceptAll = false) {
    if (acceptAll) {
      this.acceptAll();
    }
    this.cookieStore.saveCookie({ cookies: this.cookieFormContents, acceptAll });
    this.activeModal.close('OK');
  }

  private acceptAll() {
    this.cookieGroups()?.controls?.forEach(groupControl => {
      const servicesArray = groupControl.get('services') as FormArray;
      servicesArray.controls.forEach(serviceControl => {
        serviceControl.get('accepted')?.setValue(true);
      });
    });
  }

  private createCookieGroups() {
    const groups = this.groups();
    if (!groups?.length) return;

    const cookieGroupsArray = this.formGroup.get('cookieGroups') as FormArray<CookieFormArray>;

    cookieGroupsArray.clear();

    const validGroups = groups.filter(this.isValidGroup);

    validGroups.forEach(group => {
      const servicesArray = this.formBuilder.array(
        group.services
          .filter(service => service?.id)
          .map(service =>
            this.formBuilder.group({
              id: this.formBuilder.nonNullable.control(service.id),
              accepted: this.formBuilder.nonNullable.control(service?.accepted ?? false),
              dependencies: this.formBuilder.nonNullable.control(service?.dependencies?.map(d => d.id) ?? []),
            }),
          ),
      );

      const groupForm = this.formBuilder.group({
        id: this.formBuilder.nonNullable.control(group.id),
        accepted: this.formBuilder.nonNullable.control(this.checkAcceptedServices(group) ?? false),
        services: servicesArray,
      });

      cookieGroupsArray.push(groupForm);
    });
    this.cookieGroups.set(this.formGroup.get('cookieGroups') as FormArray<CookieFormArray>);
  }

  private isValidGroup(group: any) {
    return group?.id && Array.isArray(group.services);
  }

  private checkAcceptedServices(group: any) {
    return group?.services?.every(service => service?.accepted);
  }

  private handleFormUpdates() {
    this.cookieGroups()?.controls?.forEach((groupControl, groupIndex) => {
      const groupAcceptedControl = groupControl.get('accepted');
      if (groupAcceptedControl) {
        const sub = groupAcceptedControl.valueChanges
          .pipe(
            tap((parentValue: boolean) => {
              const servicesArray = groupControl.get('services') as FormArray;
              servicesArray.controls.forEach(serviceControl => {
                serviceControl.get('accepted')?.setValue(parentValue, { emitEvent: false });
              });
            }),
          )
          .subscribe();
        this.subscription.add(sub);
      }

      const servicesArray = groupControl.get('services') as FormArray;
      servicesArray.controls.forEach(serviceControl => {
        const serviceAcceptedControl = serviceControl.get('accepted');
        if (serviceAcceptedControl) {
          const sub = serviceAcceptedControl.valueChanges
            .pipe(
              tap(() => {
                const allServicesAccepted = servicesArray.controls.every(sc => sc.get('accepted')?.value === true);
                groupControl.get('accepted')?.setValue(allServicesAccepted, { emitEvent: false });
              }),
            )
            .subscribe();
          this.subscription.add(sub);
        }
      });
    });
  }
}
